import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Form, Image, Input, Tooltip, Upload, Row, Col, message, Select, TimePicker, DatePicker, Radio, RadioChangeEvent } from "antd";
import { Icon } from "@iconify/react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { OptionType } from './DynamicForm';
import RichTextEditor from '../Richtext/Richtext';
import moment from 'moment';
import { SketchPicker } from 'react-color';

const { Item } = Form;
const { TextArea } = Input;
const { Option } = Select;

interface DynamicFormArrayProps {
  field: any;
  initialValues: any[];
  onDataChange: (data: { rows: any[], rowConfig: any }) => void; 
}

const DynamicFormArray: React.FC<DynamicFormArrayProps> = ({ field, initialValues, onDataChange }) => {
  const getContainer = (triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement
  const [form] = Form.useForm();
  const [rows, setRows] = useState(initialValues || []);
  const [rowConfigs, setRowConfigs] = useState(field.rowConfig || {});
  const [options, setOptions] = useState<Record<string, OptionType[]>>({});
  console.log("rowConfigsssssssssss", rowConfigs, rows);

  const notifyParent = () => {
    onDataChange({
      rows: rows,
      rowConfig: rowConfigs
    });
  };

  useEffect(() => {
    notifyParent();
  }, [rows, rowConfigs]);

// Generate row configuration dynamically based on initialValues or rowConfig
const getRowConfig = (index: number) => {
  if (rowConfigs[index]) {
    return rowConfigs[index];
  }

  const template = Object.keys(rowConfigs).length > 0
    ? rowConfigs[Math.max(...Object.keys(rowConfigs).map(Number))]
    : {
        fields: Object.keys(initialValues[index] || {}).filter(key => key !== 'id').map(key => ({
          name: `field[${index}].${key}`,
          type: "input", // Default to input; adjust based on your needs
          label: `${key.charAt(0).toUpperCase() + key.slice(1)} ${index + 1}`,
          placeholder: `Enter ${key}`,
          validation: { required: false },
          className: "col-span-6",
        })),
      };

  const newConfig = JSON.parse(JSON.stringify(template));
  newConfig.fields = newConfig.fields.map((fieldConfig: any) => {
    const updatedField = { ...fieldConfig };
    const nameParts = updatedField.name.match(/([a-zA-Z]+)\[\d+\]\.([a-zA-Z]+)/);
    if (nameParts) {
      const baseName = nameParts[1];
      const suffix = nameParts[2];
      updatedField.name = `${baseName}[${index}].${suffix}`;
    }
    if (updatedField.label && /\d+/.test(updatedField.label)) {
      updatedField.label = updatedField.label.replace(/\d+/, `${index + 1}`);
    }
    return updatedField;
  });

  return newConfig;
};

  const { control, handleSubmit, getValues, setValue, trigger, formState: { errors }, watch } = useForm({
    defaultValues: initialValues.reduce((acc, row, index) => {
      const rowConfig = getRowConfig(index);

      rowConfig.fields.forEach((fieldConfig: any) => {
        const propertyName = fieldConfig.name.split(".").pop();
        acc[fieldConfig.name] = row[propertyName] || "";
      });

      return acc;
    }, {}),
  });

    useEffect(() => {
      const loadDynamicOptions = async () => {
  
        const fieldsWithLoadOptions = field?.filter((field:any) => field.loadOptions);
    
        for (const field of fieldsWithLoadOptions) {
          try {
            const dynamicOptions = await field.loadOptions?.();
            setOptions(prevOptions => ({
              ...prevOptions,
              [field.name]: dynamicOptions,
            }));
          } catch (error) {
            console.error(`Error loading options for ${field.name}:`, error);
          }
        }
      };
    
      loadDynamicOptions();
    }, [field]); 

    const onFinish = (values: any) => {
      const formattedData: any[] = rows.map((row, index) => {
        const rowData:any = { id: row.id };
        const rowConfig = getRowConfig(index);
        rowConfig.fields.forEach((fieldConfig: any) => {
          const propertyName = fieldConfig.name.split(".").pop();
          rowData[propertyName] = values[fieldConfig.name] ?? row[propertyName] ?? "";
        });
        return rowData;
      });
  
      console.log("Formatted form data:", formattedData);
      setRows(formattedData); 
    };

    const addRow = () => {
      const newIndex = rows.length;
      const newRow:any = { id: `new-${newIndex + 1}` };
      const newRowConfig = getRowConfig(newIndex);
      newRowConfig.fields.forEach((fieldConfig: any) => {
        const propertyName = fieldConfig.name.split(".").pop();
        newRow[propertyName] = "";
      });
  
      setRows([...rows, newRow]);
      setRowConfigs({ ...rowConfigs, [newIndex]: newRowConfig });
      field.rowConfig = { ...rowConfigs, [newIndex]: newRowConfig };
  
      newRowConfig.fields.forEach((fieldConfig: any) => {
        setValue(fieldConfig.name, "");
      });
    };
  
    const removeRow = (index: number) => {
      if (rows.length <= 1) {
        message.error('At least one row is mandatory');
        return;
      }
      const newRows = rows.filter((_, i) => i !== index);
      setRows(newRows);
  
      const updatedRowConfigs = { ...rowConfigs };
      delete updatedRowConfigs[index];
      const reindexedRowConfigs: any = {};
      newRows.forEach((_, newIndex) => {
        reindexedRowConfigs[newIndex] = getRowConfig(newIndex);
      });
      setRowConfigs(reindexedRowConfigs);
      field.rowConfig = reindexedRowConfigs;
  
      // Reindex form values
      const newFormValues: any = {};
      newRows.forEach((row, newIndex) => {
        const rowConfig = getRowConfig(newIndex);
        rowConfig.fields.forEach((fieldConfig: any) => {
          const propertyName = fieldConfig.name.split(".").pop();
          newFormValues[fieldConfig.name] = row[propertyName] ?? "";
        });
      });
      Object.entries(newFormValues).forEach(([fieldName, value]) => {
        setValue(fieldName, value);
      });
    }

  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleNumericInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    input.value = input.value.replace(/[^0-9]/g, '');
  };

  const formatPhoneNumber = (value: string) => {
    if (!value) return '';
    return value.trim();
  };

  const renderField = (field: any, rowIndex: number) => {
    if (field.showField) {
      const currentValues = getValues();
      if (!field.showField(currentValues)) {
        return null;
      }
    }
  
    switch (field.type) {
      case "input":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              console.log("inputvaluessssss", controllerField);
              return (
                <>
                  <Input
                    className="h-10"
                    {...controllerField}
                    placeholder={field.placeholder}
                    disabled={field.editable === false}
                    maxLength={field?.name.includes("nric") ? 12 : undefined}
                    type={field?.name.includes("nric") ? "number" : undefined}
                    onChange={(e) => {
                      controllerField.onChange(e);
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = e.target.value;
                      setRows(updatedRows);
                    }}
                  />
                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon icon="mdi:information-outline" color="#666" width="16" height="16" />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );
  
      case "mobile":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const value = formatPhoneNumber(controllerField.value);
              return (
                <>
                  <PhoneInput
                    country={"my"}
                    value={value}
                    onChange={(value, country: any, e, formattedValue) => {
                      if (!value || value.toLowerCase().startsWith("null")) {
                        const formattedPhone = `+${country?.dialCode} ${value?.replace(/^null/i, "") || ""}`;
                        controllerField.onChange(formattedPhone);
                        const updatedRows = [...rows];
                        const propertyName = field.name.split(".").pop();
                        updatedRows[rowIndex][propertyName] = formattedPhone;
                        setRows(updatedRows);
                        return;
                      }
                      const cleanValue = value.replace(/\s+/g, "");
                      const countryCode = country?.dialCode || "60";
                      const number = cleanValue.replace(new RegExp(`^\\+?${countryCode}`), "");
                      const formattedPhone = `+${countryCode} ${number}`;
                      controllerField.onChange(formattedPhone);
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = formattedPhone;
                      setRows(updatedRows);
                    }}
                    enableSearch
                    inputStyle={{ width: "100%", height: "40px" }}
                    dropdownStyle={{ zIndex: 1000 }}
                    disabled={field.editable === false}
                  />
                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon icon="mdi:information-outline" color="#666" width="16" height="16" />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );
  
      case "number":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <Input
                className="h-10"
                {...controllerField}
                type="text"
                placeholder={field.placeholder}
                onInput={handleNumericInput}
                maxLength={field.name.includes("nric") ? 12 : undefined}
                onChange={(e) => {
                  controllerField.onChange(e);
                  const updatedRows = [...rows];
                  const propertyName = field.name.split(".").pop();
                  updatedRows[rowIndex][propertyName] = e.target.value;
                  setRows(updatedRows);
                }}
              />
            )}
          />
        );
  
      case "textarea":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <TextArea
                {...controllerField}
                placeholder={field.placeholder}
                onChange={(e) => {
                  controllerField.onChange(e);
                  const updatedRows = [...rows];
                  const propertyName = field.name.split(".").pop();
                  updatedRows[rowIndex][propertyName] = e.target.value;
                  setRows(updatedRows);
                }}
              />
            )}
          />
        );
  
      case "checkbox":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <Checkbox
                {...controllerField}
                checked={!!controllerField.value}
                onChange={(e) => {
                  controllerField.onChange(e.target.checked);
                  const updatedRows = [...rows];
                  const propertyName = field.name.split(".").pop();
                  updatedRows[rowIndex][propertyName] = e.target.checked;
                  setRows(updatedRows);
                }}
              >
                {field.label}
              </Checkbox>
            )}
          />
        );
  
      case "file":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...field.validation,
              validate: (value) => {
                if (!field.validation?.required) return true;
                if (!value || value?.length === 0 || !(value[0]?.url || value[0]?.originFileObj)) {
                  return `${field.label} is required`;
                }
                return true;
              },
            }}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const hasFile =
                controllerField?.value &&
                controllerField?.value?.length > 0 &&
                (controllerField?.value?.[0]?.url || controllerField?.value?.[0]?.originFileObj);
              const file = controllerField?.value?.[0];
              const fileType = file?.type?.toLowerCase();
              const fileName = file?.name?.toLowerCase();
  
              const isImage =
                fileType?.startsWith("image/") ||
                fileName?.endsWith(".png") ||
                fileName?.endsWith(".jpg") ||
                fileName?.endsWith(".jpeg") ||
                fileName?.endsWith(".gif");
  
              const isVideo =
                fileType?.startsWith("video/") ||
                fileName?.endsWith(".mp4") ||
                fileName?.endsWith(".mov") ||
                fileName?.endsWith(".avi") ||
                fileName?.endsWith(".mkv");
  
              const isPdf = fileType === "application/pdf" || fileName?.endsWith(".pdf");
  
              const handleDelete = () => {
                setValue(field.name, []);
                const updatedRows = [...rows];
                const propertyName = field.name.split(".").pop();
                updatedRows[rowIndex][propertyName] = [];
                setRows(updatedRows);
              };
  
              const renderFile = () => {
                const fileSrc = file?.url || (file?.preview as string);
                const deleteIcon = (
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      borderRadius: "50%",
                      padding: "4px",
                      cursor: "pointer",
                    }}
                    onClick={handleDelete}
                  >
                    <Icon icon="mdi:close" color="#fff" width="16" height="16" />
                  </div>
                );
  
                if (isImage) {
                  return (
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <Image preview={false} src={fileSrc} style={{ maxWidth: "200px" }} />
                      {deleteIcon}
                    </div>
                  );
                }
  
                if (isVideo) {
                  return (
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <video controls style={{ display: "inline-block", maxWidth: "200px" }} src={fileSrc} />
                      {deleteIcon}
                    </div>
                  );
                }
  
                if (isPdf) {
                  return (
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <embed
                        src={fileSrc}
                        type="application/pdf"
                        style={{ width: "100%", height: "400px", border: "1px solid #ddd", borderRadius: "8px" }}
                      />
                      {deleteIcon}
                    </div>
                  );
                }
  
                return null;
              };
  
              return (
                <>
                  <Upload
                    beforeUpload={() => false}
                    fileList={Array.isArray(controllerField.value) ? controllerField.value : []}
                    showUploadList={false}
                    accept={field?.usedFileType || ""}
                    onChange={async (info: any) => {
                      const newFile = info.fileList[info.fileList.length - 1];
                      if (newFile) {
                        if (!newFile.url && !newFile.preview) {
                          newFile.preview = await getBase64(newFile.originFileObj);
                        }
                        const newFileList = [newFile];
                        setValue(field.name, newFileList, { shouldValidate: true });
                        const updatedRows = [...rows];
                        const propertyName = field.name.split(".").pop();
                        updatedRows[rowIndex][propertyName] = newFileList;
                        setRows(updatedRows);
                        trigger(field.name);
                      } else {
                        setValue(field.name, [], { shouldValidate: true });
                        const updatedRows = [...rows];
                        const propertyName = field.name.split(".").pop();
                        updatedRows[rowIndex][propertyName] = [];
                        setRows(updatedRows);
                        trigger(field.name);
                      }
                    }}
                  >
                    <Tooltip title={`Click the button & ` + field.note || ""} open={field.note ? undefined : false}>
                      <Button className="gap-2 h-10" icon={<Icon icon="bi:upload" />}>
                        Upload {field.label}
                      </Button>
                    </Tooltip>
                  </Upload>
                  {hasFile && <div style={{ marginTop: "16px" }}>{renderFile()}</div>}
                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon icon="mdi:information-outline" color="#666" width="16" height="16" />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );
  
      case "multiFile":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...field.validation,
              validate: (value) => {
                if (!value || value.length === 0) {
                  return `${field.label} is required`;
                }
                return true;
              },
            }}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const files = controllerField?.value || [];
  
              const handleDelete = (index: number) => {
                const newFiles = files.filter((_: any, i: number) => i !== index);
                setValue(field.name, newFiles, { shouldValidate: true });
                const updatedRows = [...rows];
                const propertyName = field.name.split(".").pop();
                updatedRows[rowIndex][propertyName] = newFiles;
                setRows(updatedRows);
              };
  
              const renderFile = (file: any, index: number) => {
                const fileSrc = file?.url || file?.preview;
                const fileType = file?.type?.toLowerCase();
                const fileName = file?.name?.toLowerCase();
  
                const isImage =
                  fileType?.startsWith("image/") ||
                  fileName?.endsWith(".png") ||
                  fileName?.endsWith(".jpg") ||
                  fileName?.endsWith(".jpeg") ||
                  fileName?.endsWith(".gif");
  
                const isVideo =
                  fileType?.startsWith("video/") ||
                  fileName?.endsWith(".mp4") ||
                  fileName?.endsWith(".mov") ||
                  fileName?.endsWith(".avi") ||
                  fileName?.endsWith(".mkv");
  
                const isPdf = fileType === "application/pdf" || fileName?.endsWith(".pdf");
  
                return (
                  <div key={index} style={{ position: "relative", display: "inline-block", margin: "8px" }}>
                    {isImage && <Image preview={false} src={fileSrc} style={{ maxWidth: "100px" }} />}
                    {isVideo && <video controls style={{ maxWidth: "100px" }} src={fileSrc} />}
                    {isPdf && (
                      <embed
                        src={fileSrc}
                        type="application/pdf"
                        style={{ width: "100px", height: "100px", border: "1px solid #ddd", borderRadius: "8px" }}
                      />
                    )}
                    <div
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        borderRadius: "50%",
                        padding: "4px",
                        cursor: "pointer",
                      }}
                      onClick={() => handleDelete(index)}
                    >
                      <Icon icon="mdi:close" color="#fff" width="16" height="16" />
                    </div>
                  </div>
                );
              };
  
              return (
                <>
                  <Upload
                    beforeUpload={() => false}
                    fileList={files}
                    multiple
                    showUploadList={false}
                    accept={field?.usedFileType || ""}
                    onChange={async (info: any) => {
                      const newFiles = await Promise.all(
                        info.fileList.map(async (file: any) => {
                          if (!file.url && !file.preview) {
                            file.preview = await getBase64(file.originFileObj);
                          }
                          return file;
                        })
                      );
                      setValue(field.name, newFiles, { shouldValidate: true });
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = newFiles;
                      setRows(updatedRows);
                      trigger(field.name);
                    }}
                  >
                    <Tooltip title={`Click the button & ` + field.note || ""} open={field.note ? undefined : false}>
                      <Button className="gap-2" icon={<Icon icon="bi:upload" />}>
                        Upload {field.label}
                      </Button>
                    </Tooltip>
                  </Upload>
                  {files.length > 0 && (
                    <div style={{ marginTop: "16px", display: "flex", flexWrap: "wrap" }}>{files.map(renderFile)}</div>
                  )}
                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon icon="mdi:information-outline" color="#666" width="16" height="16" />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );
  
      case "select":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = Array.isArray(field.options)
                ? field.options
                : Array.isArray(options[field.name])
                ? options[field.name]
                : [];
  
              return (
                <Select
                  size="large"
                  {...controllerField}
                  value={controllerField.value}
                  placeholder={field.placeholder}
                  showSearch
                  getPopupContainer={getContainer}
                  onChange={(value) => {
                    if (
                      field.name === "epcStatus" &&
                      ["Pending Approval (New Upgrade)", "Pending Approval (New Renewal)", "Pending Approval (New EPC)"].includes(
                        value
                      )
                    ) {
                      setValue("epcStatus", null);
                    } else {
                      controllerField.onChange(value);
                    }
  
                    if (field.name === "field_module") {
                      setValue("field_sub_module", "");
                    }
  
                    if (field.onChange) {
                      field.onChange(
                        selectOptions.find((option:any) => option.value === value),
                        { setValue, getValues }
                      );
                    }
  
                    const updatedRows = [...rows];
                    const propertyName = field.name.split(".").pop();
                    updatedRows[rowIndex][propertyName] = value;
                    setRows(updatedRows);
                  }}
                >
                  {selectOptions.map((option:any) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              );
            }}
          />
        );
  
      case "date":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const today = new Date().toISOString().split("T")[0];
              return (
                <Input
                  {...controllerField}
                  type="date"
                  className="h-10"
                  placeholder={field.placeholder}
                  max={field.futureDateRestriction ? today : ""}
                  disabled={field.editable === false}
                  onChange={(e) => {
                    controllerField.onChange(e.target.value);
                    const updatedRows = [...rows];
                    const propertyName = field.name.split(".").pop();
                    updatedRows[rowIndex][propertyName] = e.target.value;
                    setRows(updatedRows);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.currentTarget.blur();
                    setTimeout(() => e.currentTarget.focus(), 0);
                    e.currentTarget.showPicker();
                  }}
                />
              );
            }}
          />
        );
  
      case "richText":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }) => {
              const value = Array.isArray(controllerField.value) ? controllerField.value.join(", ") : controllerField.value || "";
              const extractUrlFromParagraph = (text: string) => {
                const match = text.match(/<p>(https?:\/\/[^\s]+)<\/p>/);
                return match ? match[1] : null;
              };
              const imageUrl = extractUrlFromParagraph(value);
              return (
                <>
                  <RichTextEditor
                    value={imageUrl ? `<img src="${imageUrl}" alt="image" />` : value}
                    onChange={(value) => {
                      controllerField.onChange(value);
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = value;
                      setRows(updatedRows);
                    }}
                    placeholder={field.placeholder}
                  />
                  {field.note && (
                    <div style={{ marginTop: "8px", color: "#666", display: "flex", alignItems: "center", gap: "6px" }}>
                      <Icon icon="mdi:information-outline" color="#666" width="16" height="16" />
                      <span>{field.note}</span>
                    </div>
                  )}
                </>
              );
            }}
          />
        );
  
      case "time":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const defaultStart = moment("00:00:00", "HH:mm:ss");
              const defaultEnd = moment("00:00:00", "HH:mm:ss");
  
              const parseValue = (value: string) => {
                if (!value) return [defaultStart, defaultEnd];
                const [start, end] = value.split(" - ");
                return [start ? moment(start, "HH:mm:ss") : defaultStart, end ? moment(end, "HH:mm:ss") : defaultEnd];
              };
  
              const formatValue = (value: [moment.Moment, moment.Moment]) => {
                return `${value[0].format("HH:mm:ss")} - ${value[1].format("HH:mm:ss")}` || "00:00:00 - 00:00:00";
              };
  
              const [startTime, endTime] = parseValue(controllerField.value as string);
  
              return (
                <TimePicker.RangePicker
                  {...controllerField}
                  format="HH:mm:ss"
                  onChange={(value) => {
                    const formattedValue = value
                      ? formatValue([value[0] || defaultStart, value[1] || defaultEnd])
                      : formatValue([defaultStart, defaultEnd]);
                    controllerField.onChange(formattedValue);
                    const updatedRows = [...rows];
                    const propertyName = field.name.split(".").pop();
                    updatedRows[rowIndex][propertyName] = formattedValue;
                    setRows(updatedRows);
                  }}
                  value={[startTime, endTime]}
                />
              );
            }}
          />
        );
  
      case "dateTime":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              return (
                <DatePicker
                  {...controllerField}
                  format="DD/MM/YYYY HH:mm:ss"
                  showTime={{ format: "HH:mm:ss" }}
                  value={controllerField.value ? moment(controllerField.value) : null}
                  onChange={(date: any) => {
                    const formattedValue = date ? moment(date).format("YYYY-MM-DDTHH:mm:ss") : null;
                    controllerField.onChange(formattedValue);
                    const updatedRows = [...rows];
                    const propertyName = field.name.split(".").pop();
                    updatedRows[rowIndex][propertyName] = formattedValue;
                    setRows(updatedRows);
                  }}
                />
              );
            }}
          />
        );
  
      case "multicheckbox":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue={[]}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <div className={field.className}>
                {field.options?.map((option:any) => (
                  <Checkbox
                    key={option.value}
                    value={option.value}
                    checked={controllerField.value?.includes(option.value) || false}
                    onChange={(e) => {
                      const currentValue = controllerField.value || [];
                      let updatedValue;
                      if (e.target.checked) {
                        updatedValue = [...currentValue, option.value];
                      } else {
                        updatedValue = currentValue.filter((v: string) => v !== option.value);
                      }
                      controllerField.onChange(updatedValue.length > 0 ? updatedValue : null);
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = updatedValue.length > 0 ? updatedValue : null;
                      setRows(updatedRows);
                    }}
                  >
                    {option.label}
                  </Checkbox>
                ))}
              </div>
            )}
          />
        );
  
      case "radio":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <div className={field.className}>
                {field.options?.map((option:any) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    checked={controllerField.value === option.value}
                    onChange={(e) => {
                      controllerField.onChange(e.target.value);
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = e.target.value;
                      setRows(updatedRows);
                    }}
                  >
                    {option.label}
                  </Radio>
                ))}
              </div>
            )}
          />
        );
  
      case "radioWithDropdown":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = field.loadOptions ? options[field.name] || [] : field.options || [];
              const [otherValue, setOtherValue] = React.useState(controllerField.value);
              const [inputValue, setInputValue] = React.useState("");
  
              const handleRadioChange = (e: RadioChangeEvent) => {
                const selectedValue = e.target.value;
                if (selectedValue !== "Other") {
                  setOtherValue("");
                }
                controllerField.onChange(selectedValue);
                const updatedRows = [...rows];
                const propertyName = field.name.split(".").pop();
                updatedRows[rowIndex][propertyName] = selectedValue;
                setRows(updatedRows);
              };
  
              const handleDropdownChange = (value: string) => {
                setOtherValue(value);
                controllerField.onChange(value);
                const updatedRows = [...rows];
                const propertyName = field.name.split(".").pop();
                updatedRows[rowIndex][propertyName] = value;
                setRows(updatedRows);
              };
  
              const handleChange = (e: any) => {
                const value = e.target.value.replace(/\D/g, "");
                setInputValue(value);
                const updatedRows = [...rows];
                const propertyName = "nric";
                updatedRows[rowIndex][propertyName] = value;
                setRows(updatedRows);
              };
  
              return (
                <div className={field.className}>
                  {field.options?.map((option:any) => (
                    <div key={option.value} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                      <Radio
                        value={option.value}
                        checked={
                          controllerField.value === "" || controllerField.value === null || controllerField.value === undefined
                            ? false
                            : option.value === "Other"
                            ? !field.options?.some((opt:any) => opt.value === controllerField.value) || controllerField.value === "Other"
                            : controllerField.value === option.value
                        }
                        onChange={handleRadioChange}
                      >
                        {option.label}
                      </Radio>
                      {option.value !== "Other" && controllerField.value === "Malaysian" && (
                        <Form.Item
                          validateStatus={inputValue.length > 12 ? "error" : ""}
                          help={inputValue.length > 12 ? "NRIC number cannot exceed 12 digits" : ""}
                        >
                          <Input
                            className="h-10"
                            value={inputValue || ""}
                            style={{ width: 250, marginLeft: 10 }}
                            placeholder="Enter NRIC Number"
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, "");
                              setInputValue(value);
                              handleChange({ target: { value } });
                            }}
                          />
                        </Form.Item>
                      )}
                      {controllerField.value &&
                        option.value === "Other" &&
                        (!field.options?.some((opt:any) => opt.value === controllerField.value) || controllerField.value === "Other") && (
                          <Select
                            size="large"
                            style={{ width: 200, marginLeft: 10 }}
                            placeholder={field.placeholder}
                            showSearch
                            getPopupContainer={getContainer}
                            value={otherValue || undefined}
                            onChange={handleDropdownChange}
                          >
                            {selectOptions.map((option:any) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        )}
                    </div>
                  ))}
                </div>
              );
            }}
          />
        );
  
      case "color":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => (
              <div>
                <SketchPicker
                  color={controllerField.value}
                  onChange={(color: any) => {
                    controllerField.onChange(color.hex);
                    const updatedRows = [...rows];
                    const propertyName = field.name.split(".").pop();
                    updatedRows[rowIndex][propertyName] = color.hex;
                    setRows(updatedRows);
                  }}
                />
                <div style={{ marginTop: 8, fontWeight: "bold" }}>
                  Selected Color: <span style={{ color: controllerField.value }}>{controllerField.value}</span>
                </div>
              </div>
            )}
          />
        );
  
      case "dropdownWithInput":
        return (
          <Controller
            name={field.name}
            control={control}
            defaultValue=""
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const selectOptions = field.loadOptions ? options[field.name] || [] : field.options || [];
              const [selectedOption, setSelectedOption] = React.useState<string>("");
              const [customInput, setCustomInput] = React.useState("");
  
              React.useEffect(() => {
                if (controllerField.value) {
                  const isOptionExist = selectOptions.some((opt:any) => opt.value === controllerField.value);
                  if (isOptionExist) {
                    setSelectedOption(controllerField.value);
                    setCustomInput("");
                  } else {
                    setSelectedOption("Other");
                    setCustomInput(controllerField.value);
                  }
                }
              }, [controllerField.value]);
  
              const handleSelectChange = (value: string) => {
                setSelectedOption(value);
                if (value !== "Other") {
                  setCustomInput("");
                  controllerField.onChange(value);
                  const updatedRows = [...rows];
                  const propertyName = field.name.split(".").pop();
                  updatedRows[rowIndex][propertyName] = value;
                  setRows(updatedRows);
                } else {
                  controllerField.onChange("");
                  const updatedRows = [...rows];
                  const propertyName = field.name.split(".").pop();
                  updatedRows[rowIndex][propertyName] = "";
                  setRows(updatedRows);
                }
              };
  
              const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setCustomInput(value);
                controllerField.onChange(value);
                const updatedRows = [...rows];
                const propertyName = field.name.split(".").pop();
                updatedRows[rowIndex][propertyName] = value;
                setRows(updatedRows);
              };
  
              return (
                <div className={field.className}>
                  <Select
                    size="large"
                    style={{ width: "100%", marginBottom: selectedOption === "Other" ? 8 : 0 }}
                    placeholder={field.placeholder}
                    getPopupContainer={getContainer}
                    showSearch
                    value={selectedOption || undefined}
                    onChange={handleSelectChange}
                  >
                    {selectOptions.map((option:any) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                    <Option value="Other">Other</Option>
                  </Select>
  
                  {selectedOption === "Other" && (
                    <Input
                      className="h-10"
                      placeholder="Please specify"
                      value={customInput}
                      onChange={handleInputChange}
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              );
            }}
          />
        );
  
      case "dateWithCheckbox":
        return (
          <Controller
            name={field.name}
            control={control}
            rules={field.validation}
            render={({ field: controllerField }: { field: FieldValues }) => {
              const today = new Date().toISOString().split("T")[0];
              const epcStatus = watch("epcStatus");
              const epcType = watch("epcType");
              const isForRenewal = watch("isForRenewal") ?? false;
              const [checkBoxEnable, setCheckBoxEnable] = useState(false);
              const [fieldName, setFieldName] = useState("");
              const [label, setLabel] = useState("Renewal");
              const isEditable = epcType === "PRIORITY" ? true : field.editable;
  
              return (
                <div className="gap-2">
                  <Input
                    {...controllerField}
                    type="date"
                    className="h-10"
                    placeholder={field.placeholder}
                    max={field.futureDateRestriction ? today : ""}
                    disabled={!isEditable}
                    onChange={(e) => {
                      controllerField.onChange(e.target.value);
                      controllerField.onBlur();
                      const updatedRows = [...rows];
                      const propertyName = field.name.split(".").pop();
                      updatedRows[rowIndex][propertyName] = e.target.value;
                      setRows(updatedRows);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.currentTarget.blur();
                      setTimeout(() => e.currentTarget.focus(), 0);
                      e.currentTarget.showPicker();
                    }}
                  />
                  {checkBoxEnable && (
                    <Checkbox
                      checked={Boolean(isForRenewal)}
                      onChange={(e) => {
                        setValue(fieldName, e.target.checked ?? false);
                        const updatedRows = [...rows];
                        const propertyName = fieldName.split(".").pop();
                        updatedRows[rowIndex][propertyName] = e.target.checked ?? false;
                        setRows(updatedRows);
                      }}
                    >
                      {label}
                    </Checkbox>
                  )}
                </div>
              );
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Form form={form} onFinish={handleSubmit(onFinish)} layout="vertical">
      {rows.map((row, index) => {
        const rowConfig = getRowConfig(index);
        return (
          <Card
            key={row.id}
            className="mb-4"
            title={`Row ${index + 1}`}
            extra={
              <Button danger onClick={() => removeRow(index)}>
                Remove
              </Button>
            }
          >
            <Row gutter={16}>
              {rowConfig.fields.map((fieldConfig: any) => (
                <Col span={12} key={fieldConfig.name}>
                  <Item
                    name={fieldConfig.name}
                    label={fieldConfig.label}
                    validateStatus={errors[fieldConfig.name] ? "error" : ""}
                    help={errors[fieldConfig.name]?.message as string}
                  >
                    {renderField(fieldConfig, index)}
                  </Item>
                </Col>
              ))}
            </Row>
          </Card>
        );
      })}
      <div style={{ marginBottom: 16, marginTop:5 }}>
        <Button type="dashed" onClick={addRow} block icon={<Icon icon="mdi:plus" />}>
          Add Row
        </Button>
      </div>
      {/* <Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Item> */}
    </Form>
  );
};

export default DynamicFormArray;